"use strict";

exports.__esModule = true;
exports["default"] = exports.getContentClasses = void 0;

var _react = require("react");

var _body = require("../../shared/hooks/body");

/**
Sidebar Hooks, like the file name says
Handles management of open/close state
Adding/removing CSS classes to body/html
and returns CSS classes to put on the page content
for cases where the sidebar opening/closing needs to affect
the position of the page content
*/
// return css classes to attach to body element
var getContentClasses = function getContentClasses(_ref) {
  var localType = _ref.type,
      localStatus = _ref.status,
      localPosition = _ref.position;
  return ['st_sidebar__page', "st_sidebar__page--" + localType, "st_sidebar__page--" + localStatus, "st_sidebar__page--" + localPosition];
};

exports.getContentClasses = getContentClasses;

function useSidebarState(_temp) {
  var _ref2 = _temp === void 0 ? {} : _temp,
      initialStatus = _ref2.status,
      initialType = _ref2.type,
      initialPosition = _ref2.position;

  var bodyElem = (0, _react.useRef)();
  var htmlElem = (0, _react.useRef)();
  var sidebarRef = (0, _react.useRef)();

  var _useState = (0, _react.useState)(initialStatus || 'open'),
      status = _useState[0],
      setStatus = _useState[1];

  var _useState2 = (0, _react.useState)(initialType || 'permanent'),
      type = _useState2[0],
      setType = _useState2[1];

  var _useState3 = (0, _react.useState)(initialPosition || 'left'),
      position = _useState3[0],
      setPosition = _useState3[1];

  var _useState4 = (0, _react.useState)(getContentClasses({
    type: type,
    status: initialStatus,
    position: initialPosition
  })),
      contentClasses = _useState4[0],
      setContentClasses = _useState4[1];

  var afterOpen = (0, _react.useCallback)(function () {
    setStatus('open');
    setContentClasses(getContentClasses({
      type: type,
      status: 'open',
      position: position
    }));

    if (sidebarRef.current) {
      sidebarRef.current.removeEventListener('transitionend', afterOpen);
    }
  }, [type, position]);
  var afterClose = (0, _react.useCallback)(function () {
    setStatus('closed');
    setContentClasses(getContentClasses({
      type: type,
      status: 'closed',
      position: position
    }));

    if (sidebarRef.current) {
      sidebarRef.current.removeEventListener('transitionend', afterClose);
    }
  }, [type, position]);
  var afterCollapse = (0, _react.useCallback)(function () {
    setStatus('collapsed');
    setContentClasses(getContentClasses({
      type: type,
      status: 'collapsed',
      position: position
    }));

    if (sidebarRef.current) {
      sidebarRef.current.removeEventListener('transitionend', afterCollapse);
    }
  }, [type, position]);
  var clearTransitionListeners = (0, _react.useCallback)(function () {
    if (sidebarRef.current) {
      sidebarRef.current.removeEventListener('transitionend', afterOpen);
      sidebarRef.current.removeEventListener('transitionend', afterClose);
      sidebarRef.current.removeEventListener('transitionend', afterCollapse);
    }
  }, [sidebarRef, afterClose, afterOpen, afterCollapse]);
  var openSidebar = (0, _react.useCallback)(function (_temp2) {
    var _ref3 = _temp2 === void 0 ? {} : _temp2,
        _ref3$immediate = _ref3.immediate,
        immediate = _ref3$immediate === void 0 ? false : _ref3$immediate;

    if (type === 'push') {
      if (bodyElem && bodyElem.current) {
        (0, _body.disableScroll)(bodyElem.current);
      }

      if (htmlElem && htmlElem.current) {
        (0, _body.disableScroll)(htmlElem.current);
      }
    }

    if (type === 'permanent' || immediate) {
      afterOpen();
    } else {
      setContentClasses(getContentClasses({
        type: type,
        status: 'opening',
        position: position
      }));

      if (sidebarRef.current) {
        clearTransitionListeners();
        sidebarRef.current.addEventListener('transitionend', afterOpen);
      }

      setStatus('opening');
    }
  }, [setStatus, type, clearTransitionListeners, afterOpen, position]);
  var closeSidebar = (0, _react.useCallback)(function (_temp3) {
    var _ref4 = _temp3 === void 0 ? {} : _temp3,
        _ref4$immediate = _ref4.immediate,
        immediate = _ref4$immediate === void 0 ? false : _ref4$immediate;

    if (type === 'push') {
      if (bodyElem && bodyElem.current) {
        (0, _body.enableScroll)(bodyElem.current);
      }

      if (htmlElem && htmlElem.current) {
        (0, _body.enableScroll)(htmlElem.current);
      }
    }

    if (type === 'permanent' || immediate) {
      afterClose();
    } else {
      setStatus('closing');
      setContentClasses(getContentClasses({
        type: type,
        status: 'closing',
        position: position
      }));

      if (sidebarRef.current) {
        clearTransitionListeners();
        sidebarRef.current.addEventListener('transitionend', afterClose);
      }
    }
  }, [setStatus, type, clearTransitionListeners, afterClose, position]);
  var collapseSidebar = (0, _react.useCallback)(function (_temp4) {
    var _ref5 = _temp4 === void 0 ? {} : _temp4,
        _ref5$immediate = _ref5.immediate,
        immediate = _ref5$immediate === void 0 ? false : _ref5$immediate;

    if (type === 'push') {
      if (bodyElem && bodyElem.current) {
        (0, _body.disableScroll)(bodyElem.current);
      }

      if (htmlElem && htmlElem.current) {
        (0, _body.disableScroll)(htmlElem.current);
      }
    }

    if (type === 'permanent' || immediate) {
      afterCollapse();
    } else {
      setStatus('collapsing');
      setContentClasses(getContentClasses({
        type: type,
        status: 'collapsing',
        position: position
      }));

      if (sidebarRef.current) {
        clearTransitionListeners();
        sidebarRef.current.addEventListener('transitionend', afterCollapse);
      }
    }
  }, [setStatus, type, clearTransitionListeners, afterCollapse, position]);
  (0, _react.useEffect)(function () {
    bodyElem.current = document.body;
    htmlElem.current = document.documentElement;

    switch (initialStatus) {
      case 'closed':
        closeSidebar({
          immediate: true
        });
        break;

      case 'collapsed':
        collapseSidebar({
          immediate: true
        });
        break;

      default:
        openSidebar({
          immediate: true
        });
    } // when component unmounts, put body/html tags back the way they were
    // otherwise we can end up with a page "stuck" with no scrollbars


    return function cleanup() {
      if (bodyElem && bodyElem.current) {
        (0, _body.enableScroll)(bodyElem.current);
      }

      if (htmlElem && htmlElem.current) {
        (0, _body.enableScroll)(htmlElem.current);
      }
    };
  }, [initialStatus, openSidebar, closeSidebar, collapseSidebar]);
  return {
    status: status,
    openSidebar: openSidebar,
    closeSidebar: closeSidebar,
    collapseSidebar: collapseSidebar,
    sidebarRef: sidebarRef,
    contentClasses: contentClasses,
    type: type,
    setType: setType,
    setPosition: setPosition,
    sidebarProps: {
      status: status,
      componentRef: sidebarRef,
      type: type
    },
    pageProps: {
      status: status,
      type: type,
      position: position
    },
    contentProps: {
      className: contentClasses.join(' ')
    }
  };
}

var _default = useSidebarState;
exports["default"] = _default;